import { Button, Divider, InputLabel, TextField, IconButton, FormControlLabel, Checkbox } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ApiRequest } from "GlobalFunctions";
import { useState, useEffect } from "react";
import { useSnackbar } from 'notistack';
import { PiEyeSlashLight, PiEyeLight } from "react-icons/pi";
import { PiCheckLight } from "react-icons/pi";
import { PiXLight } from "react-icons/pi";

export default function PasswordChange(props: any) {
    const { setIsEditingPassword, setActiveAccountEditor } = props;
    const { enqueueSnackbar } = useSnackbar();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [passwordUpdated, setPasswordUpdated] = useState(false);
    const [signOutDevices, setSignOutDevices] = useState(false);

    const [passwordRequirements, setPasswordRequirements] = useState([
        { text: 'A minimum of 10 characters', valid: false },
        { text: 'at least one number', valid: false },
        { text: 'at least one lowercase letter', valid: false },
        { text: 'at least one uppercase letter', valid: false }
    ]);

    useEffect(() => {
        setPasswordRequirements([
            { text: 'A minimum of 10 characters', valid: newPassword.length >= 10 },
            { text: 'at least one number', valid: /\d/.test(newPassword) },
            { text: 'at least one lowercase letter', valid: /[a-z]/.test(newPassword) },
            { text: 'at least one uppercase letter', valid: /[A-Z]/.test(newPassword) }
        ]);
    }, [newPassword]);

    const validatePassword = () => {
        let valid = true;
        passwordRequirements.forEach(requirement => {
            if(!requirement.valid) valid = false;
        });

        if(!valid){
            enqueueSnackbar('New Password Is Invalid', { variant: 'error' });
            return false;
        }

        if(newPassword !== confirmPassword) {
            enqueueSnackbar('Passwords Do Not Match', { variant: 'error' });
            return false;
        }

        return true;
    }

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleSubmit = async () => {
        setLoadingButton(true);
        
        // Validate required fields
        if (!currentPassword || !newPassword || !confirmPassword) {
            enqueueSnackbar('All fields are required', { variant: 'error' });
            setLoadingButton(false);
            return;
        }

        const passwordValid = validatePassword();

        if(passwordValid){
            // Prepare the request body
            const requestBody = {
                current_password: currentPassword,
                new_password: newPassword,
                sign_out_devices: signOutDevices
            };
            
            await ApiRequest({
                url: '/stores/changeStorePassword',
                method: 'POST',
                body: requestBody,
                setResponse: (response: any) => {
                    if(response.status === "success"){
                        setPasswordUpdated(true);
                        // Clear form fields
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                        setSignOutDevices(false);
                    } else {
                        enqueueSnackbar(response.message, { variant: 'error' });
                    }
                    setLoadingButton(false);
                }
            });
        } else {
            setLoadingButton(false);
        }
    };

    return (
        <div className='ModuleContainer alt_container'>
            <div className="ModuleHeader">
                <h1>Account</h1>
                <div className="ModuleActions">
                    
                </div>
            </div>
            <div className='ModuleContent'>
                <div className="account_section">
                    {!passwordUpdated ? (
                        <>
                            <div className="section_header">
                                <h2>Edit Password</h2>
                                <div className="section_actions">
                                    <Button
                                        variant="text"
                                        onClick={() => setIsEditingPassword(false)}
                                        className="alt_btn"
                                    >
                                        Cancel
                                    </Button>

                                    <LoadingButton
                                        loading={loadingButton}
                                        onClick={handleSubmit}
                                        variant="contained"
                                        color="primary"
                                        className="alt_btn"
                                    >
                                        Save Changes
                                    </LoadingButton>
                                </div>
                            </div>

                            <div>
                                <form 
                                    className='password_change_form'
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSubmit();
                                }}>
                                    <div className="textfield_wrapper">
                                        <InputLabel className="login_label">Current Password</InputLabel>
                                        <TextField
                                            variant="outlined"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            type={showPassword ? "text" : "password"}
                                            fullWidth
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton 
                                                        onClick={handleTogglePasswordVisibility}
                                                        edge="end"
                                                        className="show_password_button"
                                                    >
                                                        {showPassword ? 
                                                            <PiEyeLight size={24} color="#B9B9B9" /> : 
                                                            <PiEyeSlashLight size={24} color="#B9B9B9"/>
                                                        }
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    </div>

                                    <div className="textfield_wrapper">
                                        <InputLabel className="login_label">New Password</InputLabel>
                                        <TextField
                                            variant="outlined"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            type={showPassword ? "text" : "password"}
                                            fullWidth
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton 
                                                        onClick={handleTogglePasswordVisibility}
                                                        edge="end"
                                                        className="show_password_button"
                                                    >
                                                        {showPassword ? 
                                                            <PiEyeLight size={24} color="#B9B9B9" /> : 
                                                            <PiEyeSlashLight size={24} color="#B9B9B9"/>
                                                        }
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    </div>

                                    <div className="textfield_wrapper">
                                        <InputLabel className="login_label">Confirm Password</InputLabel>
                                        <TextField
                                            variant="outlined"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            type={showPassword ? "text" : "password"}
                                            fullWidth
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton 
                                                        onClick={handleTogglePasswordVisibility}
                                                        edge="end"
                                                        className="show_password_button"
                                                    >
                                                        {showPassword ? 
                                                            <PiEyeLight size={24} color="#B9B9B9" /> : 
                                                            <PiEyeSlashLight size={24} color="#B9B9B9"/>
                                                        }
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    </div>

                                    <ul className="password_requirements" style={{ listStyle: 'none', padding: 0 }}>
                                        {passwordRequirements.map((requirement, index) => (
                                            <li 
                                                key={index} 
                                                className="password_requirement" 
                                                style={{ 
                                                    color: requirement.valid ? '#24BD55' : '#CCCCCC',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {requirement.valid ? 
                                                    <PiCheckLight size={16} color="#24BD55" style={{marginRight: 8}} /> : 
                                                    <PiXLight size={16} color="#CCCCCC" style={{marginRight: 8}} />
                                                }
                                                {requirement.text}
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="textfield_wrapper">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={signOutDevices}
                                                    onChange={(e) => setSignOutDevices(e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label="Sign out of all other devices"
                                        />
                                    </div>
                                </form>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="section_header">
                                <h2>Edit Password</h2>
                                <div className="section_actions">
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            setIsEditingPassword(false);
                                            setActiveAccountEditor(false);
                                        }}
                                        className="alt_btn"
                                    >
                                        Return To Account
                                    </Button>
                                </div>
                            </div>

                            <div className="reset-toast-container">
                                Your password has been updated. All other devices have been signed out.
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}